import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Store, Target, ScanLine, ArrowLeft, Lock, ShieldCheck, Handshake, Plus, X } from "lucide-react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { getDealerStats, addPartner, removePartner } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const Sparkline = ({ data }) => {
  const vals = data && data.length ? data : [0];
  const max = Math.max(1, ...vals);
  return (
    <svg width={vals.length * 9} height="24" role="img" aria-label="weekly scan trend">
      {vals.map((v, i) => {
        const h = Math.max(2, Math.round((v / max) * 22));
        return <rect key={i} x={i * 9} y={24 - h} width="6" height={h} rx="1" className={v > 0 ? "fill-primary" : "fill-border"} />;
      })}
    </svg>
  );
};

const StatCard = ({ Icon, label, value, accent }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-xs font-600 uppercase tracking-[0.14em] text-muted-foreground">
      <Icon className={`h-4 w-4 ${accent || "text-primary"}`} /> {label}
    </div>
    <div className="mt-2 font-heading text-3xl font-900 text-foreground">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error | guest | denied
  const [busyKey, setBusyKey] = useState(null);

  const load = () =>
    getDealerStats()
      .then((d) => {
        setData(d);
        setState("ready");
      })
      .catch(() => setState("error"));

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setState("guest");
      return;
    }
    if (!user.is_admin) {
      setState("denied");
      return;
    }
    load();
  }, [user, loading]);

  const promote = async (dealer) => {
    setBusyKey(dealer.dealer_key);
    try {
      await addPartner(dealer.dealer_key);
      toast.success(`${dealer.dealer_name} added to partners`);
      await load();
    } catch {
      toast.error("Couldn't update partner status.");
    } finally {
      setBusyKey(null);
    }
  };

  const demote = async (dealer) => {
    setBusyKey(dealer.dealer_key);
    try {
      const res = await removePartner(dealer.dealer_key);
      if (res.is_partner) {
        toast.message("This dealer is a partner via the PARTNER_DEALERS config and can't be removed here.");
      } else {
        toast.success(`${dealer.dealer_name} removed from partners`);
      }
      await load();
    } catch {
      toast.error("Couldn't update partner status.");
    } finally {
      setBusyKey(null);
    }
  };

  const Shell = ({ children }) => (
    <div className="relative min-h-screen bg-background">
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[420px] bg-gradient-to-b from-secondary/70 via-secondary/20 to-transparent" aria-hidden="true" />
      <Header />
      <main className="relative mx-auto max-w-[1200px] px-5 py-10 sm:px-8">{children}</main>
    </div>
  );

  if (loading || state === "loading") {
    return (
      <Shell>
        <div className="flex items-center gap-3 text-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" /> Loading dashboard…
        </div>
      </Shell>
    );
  }

  if (state === "guest") {
    return (
      <Shell>
        <div data-testid="dashboard-guest" className="mx-auto max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/40">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <h1 className="font-heading text-xl font-800 text-foreground">Admin sign-in required</h1>
          <p className="mt-1 text-sm text-muted-foreground">This dashboard is restricted to the account owner.</p>
          <Button data-testid="dashboard-login-button" onClick={login} className="mt-5 h-11 w-full rounded-xl font-700">
            Sign in with Google
          </Button>
        </div>
      </Shell>
    );
  }

  if (state === "denied") {
    return (
      <Shell>
        <div data-testid="dashboard-denied" className="mx-auto max-w-md rounded-2xl border border-destructive/40 bg-card p-8 text-center">
          <h1 className="font-heading text-xl font-800 text-foreground">Not authorized</h1>
          <p className="mt-1 text-sm text-muted-foreground">Your account ({user.email}) doesn't have dashboard access.</p>
          <Link to="/" className="mt-5 inline-flex items-center gap-2 text-sm font-600 text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to scanner
          </Link>
        </div>
      </Shell>
    );
  }

  if (state === "error") {
    return (
      <Shell>
        <p className="text-sm text-destructive">Couldn't load dealer stats. Please refresh.</p>
      </Shell>
    );
  }

  const dealers = data.dealers || [];
  const candidates = dealers.filter((d) => !d.is_partner && d.scan_count >= data.threshold);

  const Row = ({ d, highlight }) => (
    <tr data-testid={`dealer-row-${d.dealer_key}`} className={`border-b border-border/60 ${highlight ? "bg-primary/[0.06]" : ""}`}>
      <td className="px-4 py-3">
        <div className="font-600 text-foreground">{d.dealer_name}</div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`font-heading text-lg font-800 ${d.scan_count >= data.threshold && !d.is_partner ? "text-primary" : "text-foreground"}`}>
          {d.scan_count}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-center"><Sparkline data={d.trend} /></div>
      </td>
      <td className="px-4 py-3 text-center">
        {d.is_partner ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-1 text-xs font-700 text-success">
            <ShieldCheck className="h-3.5 w-3.5" /> Partner
          </span>
        ) : d.scan_count >= data.threshold ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-700 text-primary ring-1 ring-primary/40">
            <Target className="h-3.5 w-3.5" /> Prospect
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Tracking</span>
        )}
      </td>
      <td className="px-4 py-3 text-center text-xs text-muted-foreground">{fmtDate(d.first_scan_at)}</td>
      <td className="px-4 py-3 text-center text-xs text-muted-foreground">{fmtDate(d.last_scan_at)}</td>
      <td className="px-4 py-3 text-right">
        {d.is_partner ? (
          <button
            data-testid={`remove-partner-${d.dealer_key}`}
            onClick={() => demote(d)}
            disabled={busyKey === d.dealer_key}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-600 text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive disabled:opacity-50"
          >
            {busyKey === d.dealer_key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Remove
          </button>
        ) : (
          <button
            data-testid={`add-partner-${d.dealer_key}`}
            onClick={() => promote(d)}
            disabled={busyKey === d.dealer_key}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-700 text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busyKey === d.dealer_key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add partner
          </button>
        )}
      </td>
    </tr>
  );

  const Head = () => (
    <tr className="border-b border-border text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
      <th className="px-4 py-2 font-600">Dealer</th>
      <th className="px-4 py-2 text-center font-600">Scans</th>
      <th className="px-4 py-2 text-center font-600">Trend (8 wk)</th>
      <th className="px-4 py-2 text-center font-600">Status</th>
      <th className="px-4 py-2 text-center font-600">First scan</th>
      <th className="px-4 py-2 text-center font-600">Latest scan</th>
      <th className="px-4 py-2 text-right font-600">Action</th>
    </tr>
  );

  return (
    <Shell>
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-600 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to scanner
        </Link>
        <h1 className="mt-2 font-heading text-3xl font-900 tracking-tight text-foreground sm:text-4xl">Partner Prospects</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Dealers ranked by how many contracts buyers have scanned. Any non-partner dealer that reaches{" "}
          <span className="font-700 text-primary">{data.threshold} scans</span> is a candidate to approach about the partner program.
        </p>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard Icon={Store} label="Dealers tracked" value={data.total_dealers} />
        <StatCard Icon={ScanLine} label="Total scans" value={data.total_scans} />
        <StatCard Icon={Target} label={`Prospects (${data.threshold}+)`} value={data.candidates_count} accent="text-primary" />
        <StatCard Icon={Handshake} label="Partners" value={dealers.filter((d) => d.is_partner).length} accent="text-success" />
      </div>

      {candidates.length > 0 && (
        <div data-testid="candidates-section" className="mb-8 rounded-xl border-2 border-primary/40 bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-800 text-foreground">Approach these dealers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><Head /></thead>
              <tbody>
                {candidates.map((d) => (
                  <Row key={d.dealer_key} d={d} highlight />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-3 font-heading text-lg font-800 text-foreground">All dealers</h2>
        {dealers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No scans recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><Head /></thead>
              <tbody>
                {dealers.map((d) => (
                  <Row key={d.dealer_key} d={d} highlight={!d.is_partner && d.scan_count >= data.threshold} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}
